// Compact date navigator above the dashboard. Lets users view + edit any
// past or future day's meals. "Today" pill jumps back when not on today.
//
// Date format: YYYY-MM-DD (matches todayISO from lib/storage), no timezone
// because the day is what matters — meals are bucketed by their local date.

import { useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

interface Props {
  /** Currently-selected date as YYYY-MM-DD. */
  selectedDate: string;
  /** Today's date as YYYY-MM-DD — used to label "Today" / "Yesterday" / "Tomorrow" relatively. */
  todayDate: string;
  onChange: (dateISO: string) => void;
  /** Optional: clamp how far in the future the user can jump. Default: 7 days. */
  maxFutureDays?: number;
}

export default function DateNav({ selectedDate, todayDate, onChange, maxFutureDays = 7 }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const isToday = selectedDate === todayDate;
  const isFuture = selectedDate > todayDate;

  const maxFuture = addDays(todayDate, maxFutureDays);
  const canStepForward = selectedDate < maxFuture;

  function step(deltaDays: number) {
    const next = addDays(selectedDate, deltaDays);
    if (!canStepForward && deltaDays > 0) return;
    onChange(next);
  }

  return (
    <div className="mx-4 mb-3 flex items-center gap-2">
      <button
        type="button"
        onClick={() => step(-1)}
        className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 active:bg-slate-700 flex items-center justify-center"
        aria-label="Previous day"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => setPickerOpen((v) => !v)}
        className="flex-1 h-9 rounded-lg bg-slate-900 border border-slate-800 text-slate-100 text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-800 active:bg-slate-700 relative"
        aria-label="Select date"
      >
        <Calendar className="w-3.5 h-3.5 text-slate-400" />
        <span>{formatLabel(selectedDate, todayDate)}</span>
        {isFuture && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-950/60 border border-violet-800/50 text-violet-300">
            planned
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={() => step(1)}
        disabled={!canStepForward}
        className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 active:bg-slate-700 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Next day"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {!isToday && (
        <button
          type="button"
          onClick={() => onChange(todayDate)}
          className="px-3 h-9 rounded-lg bg-emerald-500 text-slate-950 text-xs font-semibold hover:bg-emerald-400 active:bg-emerald-600"
        >
          Today
        </button>
      )}

      {/* Native date picker — hidden until opened, styled to overlay the date button.
          Using <input type="date"> for free OS-native picker on iOS / Android. */}
      {pickerOpen && (
        <input
          type="date"
          value={selectedDate}
          max={maxFuture}
          onChange={(e) => {
            const v = e.target.value;
            if (v) {
              onChange(v);
              setPickerOpen(false);
            }
          }}
          onBlur={() => setPickerOpen(false)}
          autoFocus
          className="absolute left-12 right-12 mt-9 z-50 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100"
        />
      )}
    </div>
  );
}

/** Add (or subtract) calendar days from a YYYY-MM-DD string. */
function addDays(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.split("-").map((s) => parseInt(s, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function formatLabel(dateISO: string, todayDate: string): string {
  if (dateISO === todayDate) return "Today";
  if (dateISO === addDays(todayDate, -1)) return "Yesterday";
  if (dateISO === addDays(todayDate, 1)) return "Tomorrow";

  const [y, m, d] = dateISO.split("-").map((s) => parseInt(s, 10));
  const dt = new Date(y, m - 1, d);
  const today = new Date();
  const sameYear = dt.getFullYear() === today.getFullYear();
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric"
  });
}
